import React, { useContext, useEffect, useState } from 'react';

import { ControlLabel, FormGroup } from 'react-bootstrap';
import moment from 'moment-timezone';

import Modal from '../../utils/ModalComponet';
import { FullOrderBookingOption, Order } from '../../../types/Order';
import ActivitiesAutoComplete from '../../utils/AutoComplete/ActivitiesAutoComplete';
import {
  Activity,
  Availabilities,
  BookingOption,
} from '../../../types/Activity';
import DatePicker from '../../utils/FormWidgets/DatePicker';
import OrderBookingOptionsList from '../../order/EditOrderBookingOptionList/OrderBookingOptionsList';
import ActivityBookingOptionsList from '../../order/EditOrderBookingOptionList/ActivityBookingOptionsList';
import { useFetchData } from '../../../infrastructure/hooks/useFetchData';
import * as ActivitiesService from '../../../services/ActivitiesService';
import AvailabilitiesRows from '../EditPeekAndRedeamOrderForm/components/AvailabilitiesRows';
import { ApiStatus } from '../../../infrastructure/constants/apiStatus';
import { ActivityContext } from '../../../contexts/Activity';
import ActivityHiddenWarning from '../components/ActivityHiddenWarning';

type Fields = Pick<Order, 'startDateTime' | 'endDateTime'> & {
  timeslot?: string;
};

type Props = {
  order: Order;

  activity: Activity;
  onActivitySelect: (id: string) => void;

  tz: string;
  onDateChanged: (date: string) => void;

  orderOptions: Array<FullOrderBookingOption>;
  activityOptions: BookingOption[];
  addOrderOptions: (option: Partial<BookingOption>) => void;
  deleteOrderOptions: (option: Partial<BookingOption>) => void;
  attendeesChanged: (id: string, attendeesCount: number) => void;

  closeModal: () => void;

  onSave: (fields: Fields) => void;
};

const formatDate = (date: string | Date | undefined, tz: string) =>
  moment(moment(date).tz(tz).format('YYYY-MM-DD')).toDate();

export const EditSingenuityOrderForm = (props: Props) => {
  const [apiStatus, availabilities, fetchAvailabilities] = useFetchData<
    Availabilities[]
  >();
  const [startDate, setStartDate] = useState<Date>(
    formatDate(props.order.startDateTime, props.tz)
  );
  const [primaryOption, setPrimaryOption] = useState<
    FullOrderBookingOption | undefined
  >(undefined);

  const [currentAvailability, setCurrentAvailability] = useState<
    Availabilities | undefined
  >();
  const [otherAvailabilities, setOtherAvailabilities] = useState<
    Availabilities[]
  >([]);

  const [
    selectedAvailability,
    setSelectedAvailability,
  ] = useState<Availabilities | null>(null);

  useEffect(() => {
    setPrimaryOption(
      props.orderOptions.find(
        opt => (opt.subType || 'bookingOption') === 'bookingOption'
      )
    );
  }, [props.orderOptions]);

  useEffect(() => {
    fetchAvailabilities(async () => {
      if (!primaryOption) return [];
      console.log(Number(startDate));

      return ActivitiesService.getBookingOptionAvailabilities({
        activityId: props.activity.id,
        bookingOptionId: primaryOption.id,
        startDate: moment(startDate).startOf('day').tz(props.tz, true).toDate(),
        endDate: moment(startDate).endOf('day').tz(props.tz, true).toDate(),
        tz: props.tz,
      });
    });
  }, [fetchAvailabilities, props.activity, props.tz, startDate, primaryOption]);

  useEffect(() => {
    const current = (availabilities || []).find(a => {
      return (
        a.eventDate ===
          moment(props.order.startDateTime).tz(props.tz).toISOString() ||
        a.eventDate === props.order.startDateTime
      );
    }) ?? {
      eventDate: moment(props.order.startDateTime).tz(props.tz).toISOString(),
      capacity: 0,
    };

    setCurrentAvailability(current);
    setOtherAvailabilities(
      (availabilities || []).filter(
        a =>
          a.eventDate > new Date().toISOString() &&
          a.eventDate !== current?.eventDate
      )
    );
  }, [availabilities, props.order.startDateTime, props.tz]);

  const { hidden: hiddenActivity } = useContext(ActivityContext);

  const onOrderUpdate = () => {
    if (!selectedAvailability) {
      return;
    }

    const { duration } = primaryOption ?? { duration: 60 };

    const { eventDate } = selectedAvailability;
    const m = moment(eventDate).tz(props.tz);
    const _endDateTime = moment(m).add(duration, 'minutes');
    props.onSave({
      startDateTime: m.format(),
      endDateTime: _endDateTime.format(),
      timeslot: eventDate,
    });
  };

  const onDateChange = (value?: Date) => {
    const m = moment(value).tz(props.tz);
    const date = moment(props.order.startDateTime).tz(props.tz);
    m.hours(date.hours()).minutes(date.minutes()).seconds(date.seconds());
    props.onDateChanged(m.format());
    setStartDate(formatDate(m.format(), props.tz));
  };

  return (
    <Modal
      open
      closeModal={props.closeModal}
      cancel={props.closeModal}
      action={onOrderUpdate}
      actionButtonText='Save'
      classNames={{ modal: 'edit-order-form__modal' }}
    >
      <h3>
        Edit Order {props.order.name}
        <br />
        <small>Order Status: {props.order.status}</small>
      </h3>
      <div>
        {hiddenActivity && <ActivityHiddenWarning />}
        <ControlLabel>Start Date</ControlLabel>
        <FormGroup className={'edit-order-form__date-picker'}>
          <DatePicker
            value={startDate}
            placeholder='Start Date'
            handleChange={value => onDateChange(value)}
            isClearable={false}
          />
        </FormGroup>
        <FormGroup>
          <ControlLabel>Activity</ControlLabel>
          <ActivitiesAutoComplete
            initialValue={props.activity}
            onSelect={props.onActivitySelect}
            filterData={{ singenuity: 'yes' }}
            disabled={true}
          />
        </FormGroup>
        <hr />
        {!!props.orderOptions.length && (
          <>
            <ControlLabel>Order Booking Options</ControlLabel>
            <OrderBookingOptionsList
              options={props.orderOptions}
              removeBookingOption={props.deleteOrderOptions}
              onAttendeesNumberChanged={props.attendeesChanged}
            />
          </>
        )}
        <hr />
        {!!props.activityOptions.length && (
          <>
            <ControlLabel>Activity Booking Options</ControlLabel>
            <ActivityBookingOptionsList
              options={props.activityOptions}
              addBookingOption={props.addOrderOptions}
            />
          </>
        )}
        <hr />
        <FormGroup>
          <ControlLabel>Availabilities</ControlLabel>
          <AvailabilitiesRows
            tz={props.tz}
            isPerPerson={primaryOption?.pricingModel === 'person'}
            isLoading={apiStatus === ApiStatus.loading}
            numOfAttendees={(primaryOption?.attendees || []).length}
            availabilities={otherAvailabilities}
            existingBookingOption={currentAvailability || null}
            selectedAvailability={selectedAvailability}
            setSelectedAvailability={setSelectedAvailability}
          />
        </FormGroup>
      </div>
    </Modal>
  );
};
